import FooterConfig from '@invideoio/web-shared/constants/footer';
import { PageType } from '@invideoio/web-shared/types/Analytics';
import { useContext, useEffect, useRef, useState } from 'react';

import qs from 'query-string';

import ErrorBoundary from '@components/ErrorBoundary';
import PageHeading from '@components/PageHeading';
import ToggleDurationButton from '@components/ai-pricing/ToggleDurationButton';
// import ComparePlanCTA from '@components/ai-pricing/plans/ComparePlanCTA';
// import ComparePlans from '@components/ai-pricing/plans/ComparePlans';
import PricingCards from '@components/ai-pricing/plans/PricingCards';
import StaticPricingCards from '@components/ai-pricing/plans/StaticPricingCards';
import SupportBanner from '@components/ai-pricing/support-banner/SupportBanner';
import Testimonials from '@components/ai-pricing/testimonials';
import SEO from '@components/common/SEO/SEO';
import HeaderWithCTA from '@components/header-ai';
import { Faq } from '@constants/makepage';
import useLazyload from '@hooks/useLazyload';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import FAQ from '@invideoio/web-shared/components/Faq';
import Footer from '@invideoio/web-shared/components/Footer';
import { getPricingPageGen3 } from '@lib/API';
import { StateContext } from '@store/store';
import { GetServerSidePropsContext } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { AiPricingPageType } from 'src/types';
import AppLayout from '../../layouts/AppLayout';
import dynamic from 'next/dynamic';
import { localStorageWithExpiry } from '@utils/localstorage.utils';

const Achievements = dynamic(
  () =>
    import(
      /* webpackChunkName: "Achievements" */
      '@components/achievements'
    ),
);

interface AiPricingProps {
  pageData: AiPricingPageType;
}

function AiPricing(props: AiPricingProps) {
  const { lang } = useTranslation('home');
  const { pageData } = props;

  const globalState = useContext(StateContext);

  const { platform, os } = globalState;

  const [isYearlyDuration, setIsYearlyDuration] = useState(true);

  const plansRef = useRef<HTMLDivElement>(null);

  const assetUrl = process.env.NEXT_PUBLIC_SHARED_CDN_URL || '';

  useLazyload();

  const scrollToPlans = () => {
    plansRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const urlSearchParams = window.location.search;

    const params = qs.parse(urlSearchParams);

    if (params['coupon'] && params['coupon'] !== '') {
      // set coupon in localstorage for 1 day
      localStorageWithExpiry.set('aiCoupon', params['coupon'], 60 * 24);
    }
  }, []);

  if (!pageData) {
    return null;
  }

  return (
    <AppLayout platform={platform}>
      <SEO title={pageData.SEO.Title} />
      <HeaderWithCTA isCTAIntersected={true} pageType={PageType.PRICING_PAGE} />
      <div
        className={ivclass(
          'iv-grid',
          'iv-grid-cols-12 md:iv-grid-cols-8 sm:iv-grid-cols-4',
          'iv-gap-x-40 md:iv-gap-x-25 sm:iv-gap-x-20',
          'iv-mt-100 lg:iv-mt-60 md:iv-mt-55 sm:iv-mt-40 lg:iv-px-40 md:iv-px-25 iv-px-20',
          'xl:iv-ml-10 lg:iv-ml-8',
        )}
      >
        <div
          className={ivclass(
            'iv-col-span-12 md:iv-col-span-8 sm:iv-col-span-4',
            'xl:iv-col-start-1 lg:iv-col-start-2 lg:iv-col-end-12 sm:iv-col-start-1',
            'xl:iv-px-15 lg:iv-px-20 iv-w-full',
          )}
        >
          <PageHeading
            className={ivclass(
              'iv-text-heading-2xl lg:iv-text-heading-2xl md:iv-text-heading-2xl sm:iv-text-heading-s',
              'iv-m-heading-2xl lg:iv-m-heading-2xl md:iv-m-heading-2xl sm:iv-m-heading-s',
              'iv-text-center',
              'iv-text-grey-100',
              'iv-font-extrabold',
            )}
          >
            <span className={ivclass('iv-block md:iv-inline sm:iv-inline')}>
              The right plans,{' '}
              <span
                className={ivclass('iv-title-gradient', 'iv-decoration-clone')}
              >
                for the right price{' '}
              </span>
            </span>
          </PageHeading>
          <div className={ivclass('iv-max-w-[100vw]')}>
            <div className={ivclass('xl:iv-container xl:iv-mx-auto')}>
              <div
                className={ivclass(
                  'iv-flex',
                  'sm:iv-flex-col sm:iv-gap-30',
                  'md:iv-flex-col md:iv-gap-30',
                  'iv-mt-60',
                )}
              >
                <div
                  className={ivclass(
                    'iv-flex iv-justify-center iv-items-center iv-flex-1',
                  )}
                >
                  <ToggleDurationButton
                    toggleHandler={setIsYearlyDuration}
                    toggleState={isYearlyDuration}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={ivclass('iv-max-w-[100vw]')}>
            <div className={ivclass('xl:iv-container xl:iv-mx-auto')}>
              <div className={ivclass('iv-flex iv-justify-center', 'iv-mt-40')}>
                <PricingCards
                  isYearlyDuration={isYearlyDuration}
                  goToPlanDetails={scrollToPlans}
                />
              </div>

              {/* <div className={ivclass('iv-my-30 iv-text-center')}>
                <ComparePlanCTA
                  goToPlanDetails={scrollToPlans}
                  icon={`${assetUrl}/pricing/down-arrow-blue.svg`}
                />
              </div> */}
            </div>
          </div>
        </div>
      </div>
      <div
        className={ivclass(
          'iv-grid',
          'iv-grid-cols-12 md:iv-grid-cols-8 sm:iv-grid-cols-4',
          'iv-gap-x-40 md:iv-gap-x-25 sm:iv-gap-x-20',
          'iv-mt-20 lg:iv-px-40 md:iv-px-25 iv-px-20',
          'xl:iv-ml-10 lg:iv-ml-8',
        )}
      >
        <StaticPricingCards />
      </div>
      <div className={ivclass('iv-max-w-[100vw]')}>
        <div className={ivclass('xl:iv-container xl:iv-mx-auto')}>
          <div>
            <Achievements layoutV3 />
          </div>
          <div
            className={ivclass(
              'iv-grid iv-grid-cols-12',
              'md:iv-grid-cols-8 sm:iv-grid-cols-4 iv-gap-x-40 md:iv-gap-x-25 sm:iv-gap-x-20',
            )}
          >
            <div className="iv-col-span-12 md:iv-col-span-8 sm:iv-col-span-4 xl:iv-col-start-1 lg:iv-col-start-2 lg:iv-col-end-12 sm:iv-col-start-1 iv-w-full">
              <div className="sm:iv-mt-150 iv-mt-200">
                <SupportBanner />
              </div>
              <div className="sm:iv-mt-150 iv-mt-200">
                <Testimonials testimonialState={pageData.Testimonials} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <div className={ivclass('iv-max-w-[100vw]')}>
        <div className={ivclass('xl:iv-container xl:iv-mx-auto')}>
          <div
            ref={plansRef}
            className={ivclass(
              'iv-flex sm:iv-block md:iv-block iv-justify-center',
              'iv-mt-10',
            )}
          >
            <ComparePlans
              isYearlyDuration={isYearlyDuration}
              toggleHandler={setIsYearlyDuration}
            />
          </div>
        </div>
      </div> */}
      <div className="iv-py-30">
        {pageData.FaqItems && pageData.FAQSection && (
          <ErrorBoundary>
            <FAQ
              platform={platform}
              title={{
                ...pageData.FAQSection.title,
                highlightedHeadingClass:
                  'iv-block iv-title-gradient iv-leading-tight',
              }}
              items={pageData.FaqItems}
              icons={Faq.icons}
            />
          </ErrorBoundary>
        )}
      </div>
      <Footer
        os={os}
        platform={platform}
        socialLinks={globalState.socialLinks}
        footer={FooterConfig.FOOTER_AI}
      />
    </AppLayout>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  let pageData = null;

  try {
    const homepageStrpiUrl = '/ai-pricing';
    pageData = await getPricingPageGen3(homepageStrpiUrl, context.locale);
    return { props: { pageData } };
  } catch (error) {
    console.log(error);

    return {
      notFound: true,
    };
  }
}

export default AiPricing;
